import { useLayoutEffect, useRef, useState, type CSSProperties, type RefObject } from 'react';

/**
 * Keep an anchored popover on screen.
 *
 * Dropdowns and menus are positioned against their trigger (`absolute
 * right-0` / `left-0` under a `relative` wrapper). That is fine on a
 * desktop, but on a 390px phone the trigger often sits near one edge
 * and a fixed-width panel spills off the other side, where nothing
 * can be tapped (Nick, 2026-09-12: "it pops off to the side and I
 * can't actually click on it"). Rather than re-anchoring every menu
 * by hand, measure the open panel and nudge it back inside the
 * viewport with the CSS `translate` property, which composes with any
 * Tailwind `transform` classes already on the element, so a
 * `left-1/2 -translate-x-1/2` centred popover keeps its centring and
 * just slides the few pixels it needs.
 *
 * Usage:
 *   const clamp = useViewportClamp<HTMLDivElement>(open);
 *   <div ref={clamp.ref} style={clamp.style} className="absolute right-0 …">
 *
 * Only horizontal. Vertical overflow is the page's scroll and the
 * browser handles it.
 */
export function useViewportClamp<T extends HTMLElement>(
  open: boolean,
  margin = 8,
  /** Reuse a ref the caller already attaches to the panel. */
  externalRef?: RefObject<T | null>,
): { ref: RefObject<T | null>; style: CSSProperties | undefined } {
  const ownRef = useRef<T>(null);
  const ref = externalRef ?? ownRef;
  const [dx, setDx] = useState(0);
  // The measurement has to see the panel where it would sit WITHOUT our
  // own nudge, otherwise the second pass reads the already-shifted box
  // and drifts. Track the applied shift here and subtract it.
  const applied = useRef(0);

  useLayoutEffect(() => {
    if (!open) {
      applied.current = 0;
      setDx(0);
      return;
    }
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      const left = r.left - applied.current;
      const right = r.right - applied.current;
      const vw = window.innerWidth;
      let next = 0;
      if (right > vw - margin) next = vw - margin - right;
      if (left + next < margin) next = margin - left;
      next = Math.round(next);
      if (next !== applied.current) {
        applied.current = next;
        setDx(next);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [open, margin]);

  return {
    ref,
    style: dx ? { translate: `${dx}px 0` } : undefined,
  };
}
